import { useStripePaymentV2 } from '@common/hooks/useStripePaymentV2';
import { useAppSelector } from '@common/redux/hooks';
import { appInfoSelector } from '@common/redux/selectors/app';
import { profileSelector } from '@common/redux/selectors/auth';
import { isCanvas } from '@common/utils';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import React, { useCallback, useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { Button as RNButton } from 'react-native-paper';
import { parseText } from '../func';
import { OPTIONS_CARD_ELEMENT, STATUS_STRIPE } from './constant';
import useStripePayment, {
  KEY_MODE_STRIPE,
  Props,
  ReceivedProps,
} from './hook';
import createStyles from './style';

const StripeForm = (props: Props) => {
  const [loading, setLoading] = useState(false);
  const {
    attributes,
    submitButtonText,
    enabledTitle,
    titleText,
    card,
    disabledButton,
    handleAction,
    emailBuyer,
    updateStatusCard,
    paymentAmount,
    accountConnectId,
    currency,
    paymentDescription,
  } = props;
  const { stripePayment } = useStripePaymentV2();
  const appInfo = useAppSelector(appInfoSelector);
  const stripe = useStripe();
  const elements = useElements();

  const {
    text: { status, message },
  } = card;

  const styles = createStyles({
    attributes,
    isError: status !== STATUS_STRIPE.SUCCEEDED,
  });
  const handleSubmit = async (
    stripe?: Stripe | null,
    elements?: StripeElements | null
  ) => {
    try {
      setLoading(true);
      if (!stripe || !elements) return;

      const paymentMethodRes = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement) || { token: '' },
        billing_details: {
          name: emailBuyer,
        },
      });

      const { paymentMethod, error } = paymentMethodRes || {};

      if (!paymentMethod?.id || error) {
        updateStatusCard(
          'requires_payment_method',
          error?.message || 'Payment failed'
        );
        handleAction('failedActions');
        return;
      }
      const response = await stripePayment({
        amount: paymentAmount,
        currency,
        emailBuyer: emailBuyer,
        accountConnectId,
        paymentMethodId: paymentMethod.id,
        appId: appInfo?.appId ?? '',
        paymentDescription,
      });

      if (response) {
        updateStatusCard('succeeded', 'Payment successful');
        handleAction('successActions', response?.id);
        return;
      }

      updateStatusCard('requires_payment_method', 'Payment failed');
      handleAction('failedActions');
    } catch (error: any) {
      // console.log('error', error.response);
      updateStatusCard(
        'requires_payment_method',
        error?.response?.data?.message || 'Payment failed'
      );
      handleAction('failedActions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paymentAmount < 100) {
      updateStatusCard(
        'notValidOrRequired',
        'The amount must be more than 100.'
      );
    } else {
      updateStatusCard(null, null);
    }
  }, [paymentAmount]);

  return (
    <View>
      <View>
        {enabledTitle && (
          <Text style={styles.title}>{parseText(titleText)}</Text>
        )}
        <View style={styles.wrapperInput}>
          <CardElement options={OPTIONS_CARD_ELEMENT} />
        </View>
        {!!status && <Text style={styles.text}>{message}</Text>}
        <View>
          <RNButton
            loading={loading}
            disabled={loading || disabledButton}
            mode={attributes.type}
            style={styles.submitButton}
            labelStyle={styles.submitButtonLabelStyle}
            children={submitButtonText.toUpperCase()}
            onPress={() => handleSubmit(stripe, elements)}
          />
        </View>
      </View>
    </View>
  );
};

const StripePaymentLayout = (props: Props) => {
  const [stripe, setStripe] = useState(null);
  const {
    locale,
    accountConnectId,
    updateStatusCard,
    attributes,
    stripeConfiguration,
    retrieveStripeConfiguration,
    appConfig,
    typeConnection,
    disconnectButtonText,
    connectButtonText,
    connectToStripe,
  } = props;
  const { disconnectStripe } = useStripePaymentV2();
  const profile = useAppSelector(profileSelector);
  const appInfo = useAppSelector(appInfoSelector);

  const checkStripe = useCallback(async () => {
    if (!stripeConfiguration) {
      return null;
    }
    if (!accountConnectId) {
      return null;
    }
    const resultStripe: any = await loadStripe(stripeConfiguration.publicKey, {
      stripeAccount: accountConnectId,
    });
    const isSuccessKey = [KEY_MODE_STRIPE.TEST, KEY_MODE_STRIPE.LIVE].includes(
      resultStripe?._keyMode
    );
    if (!isSuccessKey) {
      updateStatusCard(
        'notValidOrRequired',
        'Publishable Key or Secret Key is not correct. Please try again.'
      );
      return null;
    }
    setStripe(resultStripe);
    return resultStripe;
  }, [stripeConfiguration, accountConnectId]);

  const styles = createStyles({
    attributes,
    isError: false,
  });

  useEffect(() => {
    if (accountConnectId) {
      checkStripe();
    }
  }, [accountConnectId, checkStripe]);

  useEffect(() => {
    if (appInfo) {
      retrieveStripeConfiguration(appInfo.appId);
    }
  }, [appInfo]);

  // handle case anonymous
  if (typeConnection === 'anonymous') {
    if (!stripe) {
      // display nothing
      return <View></View>;
    }
    return (
      <Elements
        stripe={stripe}
        options={{
          locale: locale,
          appearance: { theme: 'stripe' },
        }}
      >
        <StripeForm {...props} />
      </Elements>
    );
  }

  if (!profile) {
    return <View>Please login to continue</View>;
  }

  if (!stripe) {
    return (
      <View>
        <RNButton
          mode={'contained'}
          style={styles.connectButton}
          labelStyle={styles.connectLabelStyle}
          children={connectButtonText}
          onPress={() => connectToStripe({ appId: appInfo?.appId ?? '' })}
        />
      </View>
    );
  }

  return (
    <View>
      <RNButton
        mode={'contained'}
        style={styles.disconnectButton}
        labelStyle={styles.disconnectLabelStyle}
        children={disconnectButtonText}
        onPress={async () => {
          const response = await disconnectStripe({
            accountConnectId,
            appId: appInfo?.appId ?? '',
          });
          setStripe(null);
        }}
      />
    </View>
  );
};

const StripePayment = (props: ReceivedProps) => {
  const _isCanvas = isCanvas();
  const propsStripePayment = useStripePayment(props);
  if (_isCanvas) {
    const styles = createStyles({
      attributes: props.attributes,
      isError: false,
    });
    return (
      <View>
        <RNButton
          mode={'contained'}
          style={styles.connectButton}
          labelStyle={styles.connectLabelStyle}
          children={propsStripePayment.connectButtonText}
          onPress={() => {}}
        />
      </View>
    );
  }
  return <StripePaymentLayout {...useStripePayment(props)} />;
};

export default StripePayment;
